import {
    Merchant,
    MerchantResponse,
    ApiResponse,
    CreateMerchantRequest,
    CreateChargeRequest,
    ReserveCalculationRequest,
    ReserveCalculationResponse,
    ICCalculationRequest,
    ICCalculationResponse,
    SSTGCalculationRequest,
    SSTGCalculationResponse,
    TransactionCalculationRequest,
    TransactionCalculationResponse,
    RefundCalculationRequest,
    RefundCalculationResponse,
    ChargebackCalculationRequest,
    ChargebackCalculationResponse,
    WireFeeCalculationRequest,
    WireFeeCalculationResponse,
    totalcostCalculationRequest,
    totalcostCalculationResponse,
    NetPayoutCalculationRequest,
    NetPayoutCalculationResponse,
    CreateBankInfoRequest,
    BankInfoResponse,
    AchFeeCalculationRequest,
    AchFeeCalculationResponse,
    SaveMerchantTransactionRequest,
    SaveMerchantTransactionResponse,
    DeleteChargeResponse,
    GetMerchantTransactionsRequest,
    MerchantTransaction,
    DeleteTransactionResponse,
    ChargeByIdResponse,
    ChargeById,
    ChargeCodeResponse,
    ChargeCodeItem,
    DeleteMerchantResponse,
    TransactionByIdResponse,
    StateByCountryCode,
} from '../types';
import { getEnvironmentConfig } from '../utils';

export class ApiService {
    private baseUrl: string;

    constructor() {
        const config = getEnvironmentConfig();
        this.baseUrl = config.API_BASE_URL;
    }

    // Fetch merchants from backend API
    async getMerchants(
        limit: number = 30,
        offset: number = 0
    ): Promise<ApiResponse<MerchantResponse>> {
        try {
            const url = `${this.baseUrl}/merchant/query?limit=${limit}&offset=${offset}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Merchants fetched successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { merchants: [] },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch merchants',
                success: false,
            };
        }
    }

    // Create a new merchant
    async createMerchant(
        merchantData: CreateMerchantRequest
    ): Promise<ApiResponse<any>> {
        try {
            const url = `${this.baseUrl}/merchant/create`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(merchantData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Merchant created successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: null,
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to create merchant',
                success: false,
            };
        }
    }
    // Delete merchant
    async deleteMerchant(id: number): Promise<DeleteMerchantResponse> {
        const url = `${this.baseUrl}/merchant/${id}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.json();
    }
    // Update merchant
    async updateMerchant(
        id: number,
        merchantData: Partial<CreateMerchantRequest>
    ): Promise<ApiResponse<any>> {
        try {
            const url = `${this.baseUrl}/merchant/${id}`;

            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(merchantData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Merchant updated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: null,
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to update merchant',
                success: false,
            };
        }
    }
    // Create a new charge
    async createCharge(
        chargeData: CreateChargeRequest
    ): Promise<ApiResponse<any>> {
        try {
            const url = `${this.baseUrl}/charge/create`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(chargeData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Charge created successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: null,
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to create charge',
                success: false,
            };
        }
    }
    // Fetch charges from backend API
    async getCharges(
        limit: number = 30,
        offset: number = 0
    ): Promise<ApiResponse<any>> {
        try {
            const url = `${this.baseUrl}/charge/query?limit=${limit}&offset=${offset}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Charges fetched successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { charges: [] },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch charges',
                success: false,
            };
        }
    }

    async getChargesByMerchantId(id: number) {
        try {
            const url = `${this.baseUrl}/charge/merchant/${id}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Charges fetched successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { charges: [] },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch charges',
                success: false,
            };
        }
    }
    // Calculate Reserve Amount
    async calculateReserve(
        reserveData: ReserveCalculationRequest
    ): Promise<ApiResponse<ReserveCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/reserve`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(reserveData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Reserve amount calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { reservefee: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate reserve amount',
                success: false,
            };
        }
    }

    // Calculate IC Amount
    async calculateIC(
        icData: ICCalculationRequest
    ): Promise<ApiResponse<ICCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/ic`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify({
                    icRate: icData.icRate,
                    grossSales: icData.grossSales,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.message ||
                        `HTTP error! status: ${response.status}`
                );
            }

            const data = await response.json();

            // Handle direct response structure: { interchange: number }
            const result = {
                interchange:
                    typeof data.interchange === 'number'
                        ? data.interchange
                        : typeof data === 'number'
                          ? data
                          : 0,
            };

            console.log('Parsed IC amount:', result);

            return {
                data: result,
                message: 'IC amount calculated successfully',
                success: true,
            };
        } catch (error) {
            console.error('Error in calculateIC:', error);
            return {
                data: { interchange: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate IC amount',
                success: false,
            };
        }
    }

    // Calculate SSTG Amount
    async calculateSSTG(
        sstgData: SSTGCalculationRequest
    ): Promise<ApiResponse<SSTGCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/sstg`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(sstgData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'SSTG amount calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { sstgfee: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate SSTG amount',
                success: false,
            };
        }
    }

    // Calculate Transaction Fee
    async calculateTransaction(
        transactionData: TransactionCalculationRequest
    ): Promise<ApiResponse<TransactionCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/transaction`;
            console.log('[API] Sending request to:', url);
            console.log('[API] Request data:', {
                transactionRate: transactionData.transactionRate,
                transactionCount: transactionData.transactionCount,
            });

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify({
                    transactionRate: transactionData.transactionRate,
                    transactionCount: transactionData.transactionCount,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.message ||
                        `HTTP error! status: ${response.status}`
                );
            }

            const data = await response.json();
            console.log('[API] Response data:', data);

            // Handle direct response structure: { transactionfee: number }
            const result = {
                transactionfee:
                    typeof data.transactionfee === 'number'
                        ? data.transactionfee
                        : typeof data === 'number'
                          ? data
                          : 0,
            };

            console.log('Parsed transaction fee amount:', result);

            return {
                data: result,
                message: 'Transaction fee calculated successfully',
                success: true,
            };
        } catch (error) {
            console.error('Error in calculateTransaction:', error);
            return {
                data: { transactionfee: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate transaction fee',
                success: false,
            };
        }
    }
    // Calculate Refund Fee
    async calculateRefund(
        refundData: RefundCalculationRequest
    ): Promise<ApiResponse<RefundCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/refund`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(refundData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Refund fee calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { totalrefund: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate refund fee',
                success: false,
            };
        }
    }
    // Calculate Chargeback Fee
    async calculateChargeback(
        chargebackData: ChargebackCalculationRequest
    ): Promise<ApiResponse<ChargebackCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/chargeback`;

            // Format the request payload to match the API's expected format
            const requestPayload = {
                chargebackCount: chargebackData.chargebackCount,
                chargebackAmount: chargebackData.chargebackAmount,
                chargebackFee: chargebackData.chargebackFee,
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(requestPayload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Chargeback fee calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { chargebackfee: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate chargeback fee',
                success: false,
            };
        }
    }
    // Calculate Wire Fee
    async calculateWireFee(
        wireData: WireFeeCalculationRequest
    ): Promise<ApiResponse<WireFeeCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/wirefee`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(wireData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Wire fee calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { wirefeeamount: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate wire fee',
                success: false,
            };
        }
    }
    // Calculate Ach Fee
    async calculateAchFee(
        achData: AchFeeCalculationRequest
    ): Promise<ApiResponse<AchFeeCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/ach`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(achData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Ach fee calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { achfeeamount: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate ach fee',
                success: false,
            };
        }
    }
    // Calculate Total Cost
    async calculatetotalcost(
        totalcostData: totalcostCalculationRequest
    ): Promise<ApiResponse<totalcostCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/totalcost`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(totalcostData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Total cost calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { totaltransactioncost: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate total cost',
                success: false,
            };
        }
    }
    // Calculate Net Payout
    async calculateNetPayout(
        netPayoutData: NetPayoutCalculationRequest
    ): Promise<ApiResponse<NetPayoutCalculationResponse>> {
        try {
            const url = `${this.baseUrl}/merchanttransaction/calculate/netpayout`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(netPayoutData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data,
                message: 'Net payout calculated successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: { netpayout: 0 },
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to calculate net payout',
                success: false,
            };
        }
    }
    // get single merchnat
    async getMerchantById(id: number): Promise<{ data: Merchant[] }> {
        try {
            const url = `${this.baseUrl}/merchant/${id}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching merchant:', error);
            return { data: [] };
        }
    }

    async createBankInfo(
        bankInfoData: CreateBankInfoRequest
    ): Promise<ApiResponse<BankInfoResponse>> {
        try {
            const url = `${this.baseUrl}/bankinfo/create`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify(bankInfoData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data,
                message: 'Bank info created successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: null as any,
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to create bank info',
                success: false,
            };
        }
    }
    // Fetch country codes
    async getCountryCodes(): Promise<ApiResponse<any>> {
        try {
            const url = `${this.baseUrl}/countrycode/`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            return {
                data: data?.data || [],
                message: 'Country codes fetched successfully',
                success: true,
            };
        } catch (error) {
            return {
                data: [],
                message:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch country codes',
                success: false,
            };
        }
    }
    async deleteCharge(id: number): Promise<DeleteChargeResponse> {
        const url = `${this.baseUrl}/charge/${id}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.json();
    }
    async saveMerchantTransaction(
        payload: SaveMerchantTransactionRequest
    ): Promise<SaveMerchantTransactionResponse> {
        let response: Response;
        let responseData: any;

        try {
            console.log(
                'Sending payload to API:',
                JSON.stringify(payload, null, 2)
            );
            response = await fetch(`${this.baseUrl}/merchanttransaction/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(payload),
            });

            // Try to parse response as JSON, but handle non-JSON responses
            try {
                responseData = await response.json();
                console.log('API Response (JSON):', responseData);
            } catch (jsonError) {
                console.warn('Failed to parse JSON response:', jsonError);
                const textResponse = await response.text();
                console.log('API Response (raw):', textResponse);
                responseData = { message: textResponse };
            }

            if (!response.ok) {
                // Log detailed error information
                const errorInfo = {
                    status: response.status,
                    statusText: response.statusText,
                    url: response.url,
                    headers: Object.fromEntries(response.headers.entries()),
                    response: responseData,
                    payload: payload,
                };

                console.error('API Error Details:', errorInfo);

                // Try to extract a meaningful error message
                let errorMessage =
                    'An error occurred while saving the transaction';
                if (responseData?.message) {
                    errorMessage = responseData.message;
                } else if (responseData?.error) {
                    errorMessage =
                        typeof responseData.error === 'string'
                            ? responseData.error
                            : JSON.stringify(responseData.error);
                } else if (response.statusText) {
                    errorMessage = response.statusText;
                }

                const error = new Error(
                    `API Error (${response.status}): ${errorMessage}`
                );
                (error as any).response = responseData;
                (error as any).status = response.status;
                throw error;
            }

            return {
                success: true,
                data: responseData,
                message:
                    responseData?.message || 'Transaction saved successfully',
            };
        } catch (error: any) {
            console.error('Error in saveMerchantTransaction:', {
                error: error.message,
                stack: error.stack,
                payload: payload,
            });
            return {
                success: false,
                message: error.message || 'Failed to save transaction',
                error: error,
            };
        }
    }

    async getMerchantTransactions(
        filterData: GetMerchantTransactionsRequest
    ): Promise<{ data: MerchantTransaction[] }> {
        let url = `${this.baseUrl}/merchanttransaction/web`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            mode: 'cors',
            body: JSON.stringify(filterData),
        });
        return response.json();
    }

    // Delete transaction
    async deleteTransaction(id: number): Promise<DeleteTransactionResponse> {
        const url = `${this.baseUrl}/merchanttransaction/${id}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.json();
    }

    // get single merchnat
    async getChargeById(id: number): Promise<ChargeByIdResponse> {
        try {
            const url = `${this.baseUrl}/charge/${id}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result: ChargeByIdResponse = await response.json();
            return result;
        } catch (error) {
            console.error('Error fetching charge:', error);
            return { data: null as any }; // fallback in case of failure
        }
    }
    async updateCharge(
        chargeData: ChargeById,
        id: number
    ): Promise<{ data: ChargeByIdResponse }> {
        try {
            const url = `${this.baseUrl}/charge/${id}`; // keep naming consistent
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(chargeData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error updating charge:', error);
            return { data: null as any };
        }
    }

    async getChargeCodes(): Promise<ChargeCodeResponse> {
        try {
            const url = `${this.baseUrl}/code`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            console.log(response, 'reposnnn');
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result: ChargeCodeResponse = await response.json();
            console.log(result, 'result');
            return result;
        } catch (error) {
            console.error('Error fetching charge codes:', error);
            return { data: [] };
        }
    }

    async getMerchantTransactionStats(filterData: {
        merchantId: number;
        firstdate: string;
        lastdate: string;
    }): Promise<{
        data: {
            week_start: string;
            total_transactions: string;
            total_chargeback: string;
        }[];
    }> {
        const url = `${this.baseUrl}/merchanttransaction/stats`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            mode: 'cors',
            body: JSON.stringify(filterData),
        });

        return response.json();
    }
    async getMerchantReport(filterData: {
        merchantId: number;
        month: string;
        year: string;
    }): Promise<any> {
        const url = `${this.baseUrl}/merchanttransaction/report/weekly`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            mode: 'cors',
            body: JSON.stringify(filterData),
        });

        return response.json();
    }
    async getTransactionById(id: number): Promise<TransactionByIdResponse> {
        try {
            const url = `${this.baseUrl}/transaction/${id}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result: TransactionByIdResponse = await response.json();
            return result;
        } catch (error) {
            console.error('Error fetching transaction:', error);
            return { data: null as any }; // fallback in case of failure
        }
    }
    async getState(id: number): Promise<StateByCountryCode> {
        const url = `${this.baseUrl}/state/${id}`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result: StateByCountryCode = await response.json();
            console.log(result, 'result');

            return result;
        } catch (error) {
            console.error('Error fetching state:', error);
            return { states: [] };
        }
    }
}

// Export singleton instance
export const apiService = new ApiService();
